// src/pages/ServicesPage.js

import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { ThemeContext } from '../context/ThemeContext';
import SectionTitle from '../components/common/SectionTitle';
import Card from '../components/common/Card';
import { serviceService } from '../api';
import PageLoader from '../components/common/PageLoader';
import ErrorDisplay from '../components/common/ErrorDisplay';

const ServicesSection = styled.section`
  padding: 5rem 2rem;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const FilterContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin: 2rem 0;
  justify-content: center;
`;

const FilterButton = styled(motion.button)`
  padding: 0.5rem 1.5rem;
  background: ${props => props.active ? props.theme.primary : 'transparent'};
  color: ${props => props.active ? 'white' : props.theme.text};
  border: 1px solid ${props => props.active ? props.theme.primary : props.theme.text + '50'};
  border-radius: 30px;
  cursor: pointer;
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.active ? props.theme.primary : props.theme.backgroundSecondary};
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const NoServicesFound = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${props => props.theme.text};
  opacity: 0.7;
  font-size: 1.2rem;
`;

const ServiceCard = styled(Card)`
  height: 100%;
  display: flex;
  flex-direction: column;
  
  .card-content {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  
  .card-footer {
    margin-top: auto;
  }
`;

const TechTag = styled.span`
  display: inline-block;
  padding: 0.2rem 0.5rem;
  background: ${props => props.theme.primary}20;
  color: ${props => props.theme.primary};
  border-radius: 4px;
  font-size: 0.8rem;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
`;

const CTAContainer = styled.div`
  text-align: center;
  margin-top: 3rem;
`;

const CTAButton = styled.a`
  display: inline-block;
  padding: 0.8rem 2rem;
  background-color: ${props => props.theme.primary};
  color: white;
  text-decoration: none;
  border-radius: 4px;
  font-weight: 600;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: ${props => props.theme.primaryDark || props.theme.primary + 'dd'};
    transform: translateY(-3px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const ServicesPage = () => {
  const { theme } = useContext(ThemeContext);
  const { t } = useTranslation();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [filteredServices, setFilteredServices] = useState([]);
  const [categories, setCategories] = useState([
    { id: 'all', name: t('allServices') }
  ]);
  
  // Chargement des services depuis l'API
  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        
        // Appel à l'API pour récupérer les services
        const response = await serviceService.getAll();
        
        // Vérifier la structure de la réponse
        let servicesData = [];
        if (response.data && Array.isArray(response.data.results)) {
          servicesData = response.data.results;
        } else if (Array.isArray(response.data)) {
          servicesData = response.data;
        }
        
        console.log("Services data from API:", servicesData);
        setServices(servicesData);
        
        // Extraire les catégories uniques des services
        if (servicesData.length > 0) {
          const uniqueCategories = [...new Set(servicesData
            .filter(service => service.category)
            .map(service => service.category)
          )];
          
          const categoryList = [
            { id: 'all', name: t('allServices') },
            ...uniqueCategories.map(cat => ({
              id: cat,
              name: t(cat) || cat // Traduire le nom de la catégorie si possible
            }))
          ];
          
          setCategories(categoryList);
        }
        
        // Mise à jour du titre de la page
        document.title = `${t('ourServices')} | PropentaTech`;
      } catch (err) {
        console.error("Error fetching services:", err);
        setError(err.response?.data?.detail || t('errorLoadingServices'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchServices();
    
    return () => {
      document.title = 'PropentaTech';
    };
  }, [t]);
  
  // Filtrer les services en fonction de la catégorie sélectionnée
  useEffect(() => {
    if (activeFilter === 'all') {
      setFilteredServices(services);
    } else {
      setFilteredServices(services.filter(service => service.category === activeFilter));
    }
  }, [activeFilter, services]);
  
  const handleFilterChange = (filterId) => {
    setActiveFilter(filterId);
  };
  
  // Extraire les technologies en tableau
  const getTechTags = (technologies) => {
    if (!technologies) return [];
    return typeof technologies === 'string' 
      ? technologies.split(',').map(tech => tech.trim())
      : Array.isArray(technologies) 
        ? technologies 
        : [];
  };
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (error) {
    return <ErrorDisplay error={error} />;
  }
  
  return (
    <ServicesSection>
      <Container>
        <SectionTitle
          title={t('ourServices')}
          subtitle={t('ourServicesDetailSubtitle')}
        />
        
        <FilterContainer>
          {categories.map((category) => (
            <FilterButton
              key={category.id}
              active={activeFilter === category.id}
              theme={theme}
              onClick={() => handleFilterChange(category.id)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {category.name}
            </FilterButton>
          ))}
        </FilterContainer>
        
        {filteredServices.length === 0 ? (
          <NoServicesFound theme={theme}>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>
                <i className="fas fa-tools"></i>
              </div>
              <h3>{t('noServicesFound')}</h3>
              <p>{t('tryDifferentFilter')}</p>
            </motion.div>
          </NoServicesFound>
        ) : (
          <ServicesGrid>
            {filteredServices.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <ServiceCard
                  image={service.image}
                  title={service.name || service.title}
                  description={
                    <>
                      <p>{service.short_description || service.description?.substring(0, 150)}</p>
                      <div style={{ marginTop: '1rem' }}>
                        {getTechTags(service.technologies).map((tech, i) => (
                          <TechTag key={i} theme={theme}>{tech}</TechTag>
                        ))}
                      </div>
                    </>
                  }
                  footer={
                    <a href={`/services/${service.id}`} className="btn btn-outline btn-small">
                      {t('learnMore')}
                    </a>
                  }
                />
              </motion.div>
            ))}
          </ServicesGrid>
        )}
        
        <CTAContainer>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <CTAButton href="/contact" theme={theme}>
              {t('requestService')}
            </CTAButton>
          </motion.div>
        </CTAContainer>
      </Container>
    </ServicesSection>
  );
};

export default ServicesPage;